import {
  createContext,
  useContext,
  useState,
  useCallback,
  type PropsWithChildren,
} from 'react';

export interface AgentPanelContextData {
  isOpen: boolean;
  /** Whether the panel was opened from a modal (affects layout shift behavior) */
  isOpenedFromModal: boolean;
  open: (fromModal?: boolean) => void;
  close: () => void;
  toggle: (fromModal?: boolean) => void;
}

const AgentPanelContext = createContext<AgentPanelContextData | undefined>(
  undefined,
);

export function AgentPanelProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState({
    isOpen: false,
    isOpenedFromModal: false,
  });
  const open = useCallback((fromModal = false) => {
    setState({ isOpen: true, isOpenedFromModal: fromModal });
  }, []);

  const close = useCallback(() => {
    setState({ isOpen: false, isOpenedFromModal: false });
  }, []);

  const toggle = useCallback((fromModal = false) => {
    setState((prev) => ({
      isOpen: !prev.isOpen,
      isOpenedFromModal: !prev.isOpen ? fromModal : false,
    }));
  }, []);

  return (
    <AgentPanelContext.Provider
      value={{
        isOpen: state.isOpen,
        isOpenedFromModal: state.isOpenedFromModal,
        open,
        close,
        toggle,
      }}
    >
      {children}
    </AgentPanelContext.Provider>
  );
}

export function useAgentPanel() {
  const context = useContext(AgentPanelContext);
  if (context === undefined) {
    throw new Error('useAgentPanel must be used within an AgentPanelProvider');
  }
  return context;
}
